import {
  useState,
  useRef,
  useEffect,
  useContext,
  useLayoutEffect,
  useMemo,
} from 'react';
import { css } from '@emotion/react';
import uuid from 'react-uuid';
import t from 'react-translate';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';

import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { LectureContentQualityStatus } from 'redux/schemas/models/ai-role-play';
import { ModalWorkflowContext } from 'lecture_pages/components/workflows/modal-workflow';
import SetUpBasics from './set-up-basics';
import SetUpInstruction from './set-up-instructions';
import useAiRolePlayHelper from '../hooks/use-ai-role-play-helper';
import { getModalBodyStyles, sharedStyles } from './styles';

export enum AIConfigStep {
  SET_UP_BASICS = 1,
  SET_UP_INSTRUCTIONS = 2,
}
export enum AIPersonalities {
  NEUTRAL = 'Neutral',
  IRRITABLE = 'Irritable',
  DISINTERESTED = 'Disinterested',
}
export { LectureContentQualityStatus };

const DEFAULT_AI_CHARACTER_OBJECTIONS = 3;
const DEFAULT_EXTRA_AI_LEARNER_GOALS = 2;

const AiRolePlayConfigModal = (props) => {
  const { onConfirm, forwardOnModalClose, onCancel } = props;
  const [step, setStep] = useState(AIConfigStep.SET_UP_BASICS);
  const [lectureContentQualityStatus, setLectureContentQualityStatus] = useState(LectureContentQualityStatus.NOT_EVALUATED);
  const { createAiRolePlayValidationSchema } = useAiRolePlayHelper();
  const { settings, setSettings } = useContext(ModalWorkflowContext);
  const dispatch = useDispatch();
  const forwardOnModalCloseRef = useRef(forwardOnModalClose);
  forwardOnModalCloseRef.current = forwardOnModalClose;

  // Memoize defaultValues to avoid wasteful uuid() calls on every render.
  // useForm only consumes defaultValues on mount, but this prevents confusion for future readers.
  const defaultValues = useMemo(() => {
    const getDefaultObjectives = (count: number) => Array.from(
      { length: count },
      () => ({ id: uuid(), text: '' }),
    );

    return {
      title: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.DEFAULT_TITLE(),
      description: '',
      contextDescription: '',
      learnerRole: '',
      aiCharacterRole: '',
      aiCharacterName: '',
      context: '',
      aiCharacterPersonality: AIPersonalities.NEUTRAL,
      aiCharacterFace: {
        source: 'preset',
        presetKey: 'default',
        displayUrl: '',
        customFaces: [],
      },
      requiredAiLearnerGoal: { id: uuid(), text: '' },
      aiCharacterObjections: getDefaultObjectives(DEFAULT_AI_CHARACTER_OBJECTIONS),
      aiLearnerGoals: getDefaultObjectives(DEFAULT_EXTRA_AI_LEARNER_GOALS),
    };
  }, []);

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(createAiRolePlayValidationSchema),
  });

  const { formState } = methods;
  const { isDirty } = formState;

  useEffect(() => {
    forwardOnModalCloseRef.current((e: Event, closeModal: Function) => {
      if (isDirty) {
        e.preventDefault();

        dispatch(openConfirmationDialog({
          onConfirm: () => closeModal(),
          cancelText: t.FORM.CANCEL(),
          confirmText: t.FORM.YES_SURE(),
          title: t.FORM.UNSAVED_CHANGES.CLOSE_WINDOW.TITLE(),
          bodyText: t.FORM.UNSAVED_CHANGES.CLOSE_WINDOW.DESCRIPTION(),
        }));
      }
    });
  }, [isDirty, dispatch]);

  useLayoutEffect(() => {
    switch (step) {
      case AIConfigStep.SET_UP_BASICS:
        setSettings((prevSettings) => (
          {
            ...prevSettings,
            modalWidth: 640,
            modalStyles: css`
            .bs4-modal-header {
              display: none;
            }
          `,
          }
        ));
        break;
      case AIConfigStep.SET_UP_INSTRUCTIONS:
        setSettings((prevSettings) => (
          {
            ...prevSettings,
            modalWidth: 1080,
            title: (mode) => t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.CREATE_TITLE(),
            modalStyles: null,
          }
        ));
        break;
      default:
        break;
    }
  }, [step, setSettings]);

  const getForm = () => {
    switch (step) {
      case AIConfigStep.SET_UP_BASICS:
        return (
          <SetUpBasics
            onCancel={onCancel}
            setStep={setStep}
            setLectureContentQualityStatus={setLectureContentQualityStatus}
          />
        );
      case AIConfigStep.SET_UP_INSTRUCTIONS:
        return (
          <SetUpInstruction
            onCancel={onCancel}
            onSubmit={onConfirm}
            setStep={setStep}
            lectureContentQualityStatus={lectureContentQualityStatus}
          />
        );
      default:
        return (
          <SetUpBasics
            onCancel={onCancel}
            setStep={setStep}
            setLectureContentQualityStatus={setLectureContentQualityStatus}
          />
        );
    }
  };

  return (
    <div css={[sharedStyles, getModalBodyStyles(step)]}>
      <FormProvider {...methods}>
        <form>
          {getForm()}
        </form>
      </FormProvider>
    </div>
  );
};

export default AiRolePlayConfigModal;
